import {onUnknownChangeHandler} from 'lib/dom'
import ColorPicker from 'lib/ui/ColorPicker'
import Select from '@material-ui/core/Select'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Box from '@material-ui/core/Box'
import Slider from 'lib/ui/Slider'
import React from 'react'
import Button from '@material-ui/core/Button'
import styled from 'styled-components'
import ComponentConfig, {
  SaveButton,
  Footer,
} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import {useToggle} from 'lib/toggle'
import {Controller, useForm, UseFormMethods} from 'react-hook-form'
import {useTemplate} from 'Event/TemplateProvider'
import {useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {BaseTemplate, Template} from 'Event/template'
import {useAutoUpdate} from 'organization/Event/TemplateEditor'
import StylingPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/StylingPanel'
import CollapsibleSection from 'lib/ui/ConfigPanel/CollapsibleSection'
import {Label} from 'lib/ui/typography'
import FontSelect from 'lib/FontSelect'
import BackgroundPicker from 'lib/ui/form/BackgroundPicker'
import Checkbox from 'lib/ui/Checkbox'
import Visible from 'lib/ui/layout/Visible'
import {
  DEFAULT_BUTTON_TEXT_COLOR,
  DEFAULT_BUTTON_BACKGROUND_COLOR,
} from 'lib/ui/Button/CustomButton'
import {CAPS} from 'lib/ui/typography/FontStyleInput'
import {
  DEFAULT_BUTTON_HEIGHT,
  DEFAULT_FONT_SIZE,
} from 'Event/Dashboard/components/NavButton'

export default function PostFormStylesConfig() {
  const {flag: showingConfig, toggle: toggleConfig} = useToggle()

  const {postFormStyles} = useTemplate()
  const update = useSaveTemplate()
  const {
    control,
    handleSubmit,
    watch,
    formState: {isDirty},
  } = useForm()

  useAutoUpdate({
    values: {postFormStyles: watch()},
    when: showingConfig,
  })

  const submit = (data: Template['postFormStyles']) => {
    update({
      postFormStyles: data,
    })

    toggleConfig()
  }

  const onClose = () => {
    toggleConfig()
  }

  return (
    <>
      <StyledEditPostFormStylesButton onClick={toggleConfig} />
      <ComponentConfig
        showing={showingConfig}
        onClose={onClose}
        title="Post Form Styles"
        onSubmit={handleSubmit(submit)}
        hasChanges={isDirty}
      >
        <StylingPanel>
          <FormFields control={control} postFormStyles={postFormStyles} />
          <InputStylesSection
            control={control}
            postFormStyles={postFormStyles}
          />
          <ButtonStylesSection
            control={control}
            watch={watch}
            postFormStyles={postFormStyles}
          />
        </StylingPanel>

        <Footer>
          <SaveButton type="submit" />
        </Footer>
      </ComponentConfig>
    </>
  )
}

function EditPostFormStylesButton(props: {
  onClick: () => void
  className?: string
}) {
  return (
    <Button
      className={props.className}
      fullWidth
      size="large"
      variant="contained"
      color="primary"
      aria-label="style post form"
      onClick={props.onClick}
    >
      Edit Post Form Styles
    </Button>
  )
}

export function FormFields(props: {
  control: UseFormMethods['control']
  postFormStyles: BaseTemplate['postFormStyles']
  namePrefix?: string
}) {
  const {control, postFormStyles, namePrefix = ''} = props

  return (
    <CollapsibleSection label="Form" storageKey="postFormStyles.config.form">
      <Controller
        name={`${namePrefix}width`}
        defaultValue={postFormStyles?.width || 100}
        control={control}
        render={({value, onChange}) => (
          <Slider
            min={1}
            max={100}
            onChange={onChange}
            valueLabelDisplay="off"
            value={value}
            unit="%"
            hideUnits
            label="Form Width"
          />
        )}
      />
      <Box mb={2}>
        <InputLabel>Form Position</InputLabel>
        <Controller
          name={`${namePrefix}position`}
          defaultValue={postFormStyles?.position}
          control={control}
          render={({value, onChange}) => (
            <Select
              value={value}
              onChange={onUnknownChangeHandler(onChange)}
              fullWidth
            >
              <MenuItem value="flex-end">Right</MenuItem>
              <MenuItem value="center">Center</MenuItem>
              <MenuItem value="flex-start">Left</MenuItem>
            </Select>
          )}
        />
      </Box>
    </CollapsibleSection>
  )
}

export function InputStylesSection(props: {
  control: UseFormMethods['control']
  postFormStyles: BaseTemplate['postFormStyles']
  namePrefix?: string
}) {
  const {control, postFormStyles, namePrefix = ''} = props
  const inputStyles = postFormStyles?.inputStyles

  return (
    <CollapsibleSection
      label="Inputs"
      storageKey="postFormStyles.config.inputStyles"
    >
      <Controller
        name={`${namePrefix}inputStyles.labelColor`}
        defaultValue={inputStyles?.labelColor}
        control={control}
        render={({value, onChange}) => (
          <ColorPicker
            label="Label Text Color"
            color={value}
            onPick={onChange}
            aria-label="label text color"
          />
        )}
      />
      <Controller
        name={`${namePrefix}inputStyles.textColor`}
        defaultValue={inputStyles?.textColor}
        control={control}
        render={({value, onChange}) => (
          <ColorPicker
            label="Input Text Color"
            color={value}
            onPick={onChange}
            aria-label="input box text color"
          />
        )}
      />
      <Controller
        name={`${namePrefix}inputStyles.helperTextColor`}
        defaultValue={inputStyles?.helperTextColor}
        control={control}
        render={({value, onChange}) => (
          <ColorPicker
            label="Helper Text Color"
            color={value}
            onPick={onChange}
            aria-label="helper text color"
          />
        )}
      />
      <Controller
        name={`${namePrefix}inputStyles.backgroundColor`}
        defaultValue={inputStyles?.backgroundColor}
        control={control}
        render={({value, onChange}) => (
          <ColorPicker
            label="Input Background Color"
            color={value}
            onPick={onChange}
            aria-label="input box background color"
          />
        )}
      />
      <Box>
        <Controller
          name={`${namePrefix}inputStyles.backgroundOpacity`}
          defaultValue={inputStyles?.backgroundOpacity}
          control={control}
          render={({value, onChange}) => (
            <Slider
              min={0}
              max={100}
              step={1}
              onChange={onChange}
              valueLabelDisplay="auto"
              value={value}
              label="Input Background Color Opacity"
              unit="%"
            />
          )}
        />
      </Box>
      <Controller
        name={`${namePrefix}inputStyles.borderColor`}
        defaultValue={inputStyles?.borderColor}
        control={control}
        render={({value, onChange}) => (
          <ColorPicker
            label="Input Border Color"
            color={value}
            onPick={onChange}
            aria-label="input box border color"
          />
        )}
      />
    </CollapsibleSection>
  )
}

export function ButtonStylesSection(props: {
  control: UseFormMethods['control']
  watch: UseFormMethods['watch']
  postFormStyles: BaseTemplate['postFormStyles']
  namePrefix?: string
}) {
  const {control, watch, postFormStyles, namePrefix = ''} = props

  const showingHoverBackgroundInput = !watch(
    `${namePrefix}buttonDisableHover`,
    postFormStyles?.buttonDisableHover,
  )

  return (
    <CollapsibleSection
      label="Buttons"
      storageKey="postFormStyles.config.buttonStyles"
    >
      <CollapsibleSection
        label="Typography"
        storageKey="postFormStyles.config.buttonStyles.typography"
      >
        <Label>Font</Label>
        <Controller
          control={control}
          name={`${namePrefix}buttonFont`}
          defaultValue={postFormStyles?.buttonFont ?? null}
          render={({value, onChange}) => (
            <FontSelect
              value={
                value ?? {
                  family: 'inherit',
                  variants: [],
                  styles: [CAPS],
                }
              }
              onChange={onChange}
            />
          )}
        />
        <Controller
          name={`${namePrefix}buttonFontSize`}
          defaultValue={postFormStyles?.buttonFontSize || DEFAULT_FONT_SIZE}
          control={control}
          render={({value, onChange}) => (
            <Slider
              min={1}
              max={100}
              onChange={onChange}
              valueLabelDisplay="off"
              value={value}
              label="Font Size"
            />
          )}
        />
        <Controller
          name={`${namePrefix}buttonColor`}
          control={control}
          defaultValue={
            postFormStyles?.buttonColor || DEFAULT_BUTTON_TEXT_COLOR
          }
          render={({value, onChange}) => (
            <ColorPicker
              label="Text Color"
              color={value}
              onPick={onChange}
              aria-label="submit button color"
            />
          )}
        />
        <Controller
          name={`${namePrefix}buttonHoverTextColor`}
          control={control}
          defaultValue={postFormStyles?.buttonHoverTextColor ?? null}
          render={({value, onChange}) => (
            <ColorPicker
              label="Text Hover Color"
              color={
                value ||
                postFormStyles?.buttonColor ||
                DEFAULT_BUTTON_TEXT_COLOR
              }
              onPick={onChange}
              aria-label="submit button hover text color"
            />
          )}
        />
      </CollapsibleSection>

      <CollapsibleSection
        label="Background"
        storageKey="postFormStyles.config.buttonStyles.background"
      >
        <Controller
          name={`${namePrefix}buttonBackgroundColor`}
          control={control}
          defaultValue={
            postFormStyles?.buttonBackgroundColor ||
            DEFAULT_BUTTON_BACKGROUND_COLOR
          }
          render={({value, onChange}) => (
            <BackgroundPicker
              label="Background"
              background={value}
              onChange={onChange}
            />
          )}
        />
        <Controller
          name={`${namePrefix}buttonDisableHover`}
          control={control}
          defaultValue={postFormStyles?.buttonDisableHover || false}
          render={({value, onChange}) => (
            <StyledCheckBox
              label="Customize Hover Background"
              checked={!value}
              onChange={(val) => onChange(!val)}
              aria-label="toggle custom hover background"
            />
          )}
        />
        <Visible when={showingHoverBackgroundInput}>
          <Controller
            name={`${namePrefix}buttonHoverBackgroundColor`}
            control={control}
            defaultValue={
              postFormStyles?.buttonHoverBackgroundColor ||
              DEFAULT_BUTTON_BACKGROUND_COLOR
            }
            render={({value, onChange}) => (
              <BackgroundPicker
                label="Hover Background"
                background={value}
                onChange={onChange}
              />
            )}
          />
        </Visible>
      </CollapsibleSection>

      <CollapsibleSection
        label="Size"
        storageKey="postFormStyles.config.buttonStyles.size"
      >
        <Controller
          name={`${namePrefix}buttonHeight`}
          defaultValue={postFormStyles?.buttonHeight ?? null}
          control={control}
          render={({value, onChange}) => (
            <Slider
              min={1}
              max={400}
              onChange={onChange}
              valueLabelDisplay="off"
              value={value ?? DEFAULT_BUTTON_HEIGHT}
              label="Height"
            />
          )}
        />
        <Controller
          name={`${namePrefix}buttonSize`}
          defaultValue={postFormStyles?.buttonSize || 100}
          control={control}
          render={({value, onChange}) => (
            <Slider
              min={1}
              max={100}
              onChange={onChange}
              valueLabelDisplay="off"
              value={value}
              unit="%"
              hideUnits
              label="Width"
            />
          )}
        />
        <Box mb={2}>
          <InputLabel>Position</InputLabel>
          <Controller
            name={`${namePrefix}buttonPosition`}
            defaultValue={postFormStyles?.buttonPosition}
            control={control}
            render={({value, onChange}) => (
              <Select
                value={value}
                onChange={onUnknownChangeHandler(onChange)}
                fullWidth
              >
                <MenuItem value="flex-end">Right</MenuItem>
                <MenuItem value="center">Center</MenuItem>
                <MenuItem value="flex-start">Left</MenuItem>
              </Select>
            )}
          />
        </Box>
      </CollapsibleSection>

      <CollapsibleSection
        label="Border"
        storageKey="postFormStyles.config.buttonStyles.border"
        noDivider
      >
        <Controller
          name={`${namePrefix}buttonBorderColor`}
          control={control}
          defaultValue={postFormStyles?.buttonBorderColor || '#000000'}
          render={({value, onChange}) => (
            <ColorPicker
              label="Color"
              color={value}
              onPick={onChange}
              aria-label="submit button border color"
            />
          )}
        />
        <Controller
          name={`${namePrefix}buttonHoverBorderColor`}
          control={control}
          defaultValue={postFormStyles?.buttonHoverBorderColor ?? null}
          render={({value, onChange}) => (
            <ColorPicker
              label="Hover Color"
              color={value || postFormStyles?.buttonBorderColor || '#000000'}
              onPick={onChange}
              aria-label="submit button hover border color"
            />
          )}
        />
        <Controller
          name={`${namePrefix}buttonBorderWidth`}
          defaultValue={postFormStyles?.buttonBorderWidth || 0}
          control={control}
          render={({value, onChange}) => (
            <Slider
              min={0}
              max={100}
              onChange={onChange}
              valueLabelDisplay="off"
              value={value}
              label="Width"
            />
          )}
        />
        <Controller
          name={`${namePrefix}buttonRadius`}
          defaultValue={postFormStyles?.buttonRadius || 0}
          control={control}
          render={({value, onChange}) => (
            <Slider
              min={0}
              max={100}
              onChange={onChange}
              valueLabelDisplay="off"
              value={value}
              label="Radius"
            />
          )}
        />
      </CollapsibleSection>
    </CollapsibleSection>
  )
}

const StyledCheckBox = styled(Checkbox)`
  margin-bottom: ${(props) => props.theme.spacing[4]};
`

const StyledEditPostFormStylesButton = styled(EditPostFormStylesButton)`
  margin-bottom: ${(props) => props.theme.spacing[3]}!important;
`
